import { formatDisplay } from "../../../view/display-format";
import type { SimMetrics, SimReport } from "./sim-lab-types";

export type SimCompareResult = {
  delta: Partial<SimMetrics>;
  summary: string[];
};

function metricDelta(a: number | undefined, b: number | undefined): number | undefined {
  if (a == null || b == null) return undefined;
  return b - a;
}

/** Pairwise metric deltas (b − a) plus human-readable summary lines. */
export function compareReports(a: SimReport, b: SimReport): SimCompareResult {
  const keys = new Set([
    ...Object.keys(a.metrics),
    ...Object.keys(b.metrics),
  ]) as Set<keyof SimMetrics>;
  const delta: Partial<SimMetrics> = {};
  const summary: string[] = [];
  for (const key of keys) {
    const d = metricDelta(a.metrics[key], b.metrics[key]);
    if (d == null || !Number.isFinite(d)) continue;
    delta[key] = d;
    const sign = d > 0 ? "+" : "";
    summary.push(`${String(key)}: ${sign}${formatDisplay(d, 1)}`);
  }
  if (summary.length === 0) {
    summary.push("no metric delta");
  }
  return { delta, summary };
}

/**
 * Instinct sanity on leap: low instinct should react slower or never open
 * dodge (higher lag and/or more time spent inside danger zones).
 */
export function instinctLeapMonotonic(
  lowInstinct: SimReport,
  highInstinct: SimReport,
): boolean {
  const lowLag = lowInstinct.metrics.dodgeReactionLagSec ?? 0;
  const highLag = highInstinct.metrics.dodgeReactionLagSec ?? 0;
  const lowDanger = lowInstinct.metrics.timeInDangerZone ?? 0;
  const highDanger = highInstinct.metrics.timeInDangerZone ?? 0;
  const lagOk = lowLag >= highLag - 1e-6;
  const dangerOk = lowDanger >= highDanger - 1e-6;
  return lagOk || dangerOk;
}
